<!--
  AvatarElement.vue

  Displays a person's avatar image with fallback to initials or icon.
  Optionally acts as a drop target for image files or URLs when editable.

  Props:
    - record: Record object (person) — uses avatar.url for the image
    - avatarUrl: Explicit image URL. Takes precedence over record.avatar.url.
    - name: Display name (used for initials fallback). Falls back to record.name
    - email: Email address — used as initials fallback (first letter of local-part)
            and for the hover title when no record is present
    - size: Avatar size ('x-small', 'small', 'normal', 'medium', 'large')
    - fixed: Disable navigation link (default: false)
    - editable: Enable drag-and-drop avatar upload (default: false)
    - icon: Font Awesome icon for the no-image fallback (default: 'user-circle')
    - iconColor: Color for the fallback icon — also tints the ring and fill.
            Ignored while an image or initials are showing.
    - fallback: Which no-image fallback to prefer — 'initials' (default) or
            'icon'. Use 'icon' where the element stands for a record of any
            type (e.g. the record header), so an org or deal reads as its type
            rather than as a pair of letters.

  Emits:
    - avatar-updated: Fired after a successful avatar upload with the new URL

  Fallback chain:
    1. record.avatar.url
    2. Initials derived from name, then email local-part (unless fallback="icon")
    3. The `icon` prop, drawn inset rather than filling the circle, tinted with
       `iconColor` when one is given
-->
<template>
  <div
    class="avatar-element"
    :class="avatarClass"
    :style="iconColor && showIcon ? { '--avatar-icon-color': iconColor } : undefined"
    @dragenter.prevent.stop="onDragEnter"
    @dragover.prevent.stop="onDragOver"
    @dragleave.prevent.stop="onDragLeave"
    @drop.prevent.stop="onDrop"
  >
    <component
      :is="!fixed && record ? 'record-link' : 'span'"
      class="avatar-content"
      v-bind="!fixed && record ? { to: record } : { title: titleText || undefined }"
    >
      <img
        v-if="resolvedAvatarUrl"
        :src="resolvedAvatarUrl"
        :alt="displayName"
        class="avatar-img"
        loading="lazy"
        @error="onImageError"
      />
      <span v-else-if="showInitials" class="avatar-initials">{{ initials }}</span>
      <font-awesome-icon
        v-else
        :icon="icon"
        class="avatar-icon"
        :class="{ 'is-inset': iconIsInset }"
      />
    </component>

    <!-- Drop / upload overlay -->
    <span v-if="editable && (isDragOver || uploading)" class="avatar-overlay">
      <font-awesome-icon v-if="uploading" icon="spinner" spin />
      <font-awesome-icon v-else icon="camera" />
    </span>

    <!-- Optional status badge (e.g. team membership, unknown contact) -->
    <span v-if="$slots.badge" class="avatar-badge">
      <slot name="badge" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { toast } from "vue-sonner"
import type { RecordFieldsFragment } from "@auger/core/gql/types"
import { servedUrl, useAssets } from "@/composables/useAssets"
import { useAvatarWrite } from "@/composables/useAvatarWrite"
import { client } from "@/init/apollo"

interface Props {
  record?: AvatarRecord
  avatarUrl?: string
  name?: string
  email?: string
  size?: string
  fixed?: boolean
  editable?: boolean
  icon?: string
  iconColor?: string
  fallback?: "initials" | "icon"
}

type AvatarRecord = {
  id?: number | string
  name?: string | null
  type?: string
  slug?: string | null
  avatar?: { url?: string | null } | null
}

const props = withDefaults(defineProps<Props>(), {
  record: undefined,
  avatarUrl: undefined,
  name: undefined,
  email: undefined,
  size: "normal",
  fixed: false,
  editable: false,
  icon: "user-circle",
  iconColor: undefined,
  fallback: "initials",
})

const emit = defineEmits<{
  "avatar-updated": [url: string]
}>()

const imgFailed = ref(false)
const isDragOver = ref(false)
const uploading = ref(false)
let dragCounter = 0

const displayName = computed(() => props.name || props.record?.name || "")

const titleText = computed(() => displayName.value || props.email || "")

const resolvedAvatarUrl = computed(() => {
  if (imgFailed.value) return null
  const url = props.avatarUrl || props.record?.avatar?.url || null
  return servedUrl(url)
})

const initials = computed(() => {
  const name = displayName.value.trim()
  if (name) {
    const parts = name.split(/\s+/)
    if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
    return name[0]!.toUpperCase()
  }
  const localPart = props.email?.split("@")[0]
  return localPart ? localPart[0]!.toUpperCase() : null
})

const showInitials = computed(() => props.fallback === "initials" && !!initials.value)

const showIcon = computed(() => !resolvedAvatarUrl.value && !showInitials.value)

// user-circle is itself a filled circle, so it fills the element; any other
// glyph needs room around it to read as a mark inside a circle.
const iconIsInset = computed(() => props.icon !== "user-circle")

const sizeClass = computed(() => `is-${props.size}`)

const avatarClass = computed(() => [
  sizeClass.value,
  {
    "is-editable": props.editable,
    "is-drag-over": isDragOver.value,
    "is-uploading": uploading.value,
    "is-tinted": showIcon.value && !!props.iconColor,
  },
])

function onImageError() {
  imgFailed.value = true
}

// --- Drop target logic (only active when editable) ---

const { uploadFile, uploadUrl } = useAssets({ client, toast })
const { attachAndUpdate } = useAvatarWrite({ client, toast })

function isDropRelevant(event: DragEvent) {
  if (!props.editable) return false
  const types = event.dataTransfer?.types || []
  return types.includes("Files") || types.includes("text/uri-list") || types.includes("text/plain")
}

function onDragEnter(event: DragEvent) {
  if (!isDropRelevant(event)) return
  dragCounter++
  if (dragCounter === 1) isDragOver.value = true
}

function onDragLeave(event: DragEvent) {
  if (!isDropRelevant(event)) return
  dragCounter--
  if (dragCounter === 0) isDragOver.value = false
}

function onDragOver(event: DragEvent) {
  if (!isDropRelevant(event)) return
  if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
}

type PersistedRecord = AvatarRecord & Pick<RecordFieldsFragment, "id">

function isPersistedRecord(record: AvatarRecord | undefined): record is PersistedRecord {
  return typeof record?.id === "number"
}

function onDrop(event: DragEvent) {
  if (!isDropRelevant(event)) return
  dragCounter = 0
  isDragOver.value = false

  const record = props.record
  if (!isPersistedRecord(record)) return

  // Prefer file drops (e.g. dragged from Finder)
  if (event.dataTransfer?.types?.includes("Files")) {
    const files = Array.from(event.dataTransfer.files || [])
    const imageFile = files.find((f) => f.type.startsWith("image/"))
    if (imageFile) {
      handleFileUpload(imageFile, record)
      return
    }
  }

  // Fall back to URL drops (dragged from browser)
  const url =
    event.dataTransfer?.getData("text/uri-list") || event.dataTransfer?.getData("text/plain")
  if (url && (url.startsWith("http://") || url.startsWith("https://"))) {
    handleUrlUpload(url, record)
  }
}

function handleFileUpload(file: File, record: PersistedRecord) {
  uploading.value = true
  uploadFile(file, { record })
    .then((asset) => attachAndUpdate(asset, record))
    .then((assetUrl) => {
      imgFailed.value = false
      if (assetUrl) emit("avatar-updated", assetUrl)
    })
    // upload failures are toasted by useAssets; attach/update failures are now
    // toasted by useAvatarWrite itself — this catch only avoids an unhandled
    // rejection, it must not toast again.
    .catch(() => {})
    .finally(() => {
      uploading.value = false
    })
}

function handleUrlUpload(url: string, record: PersistedRecord) {
  uploading.value = true
  uploadUrl(url, { record, origin: "avatar_drop" })
    .then((asset) => attachAndUpdate(asset, record))
    .then((assetUrl) => {
      imgFailed.value = false
      if (assetUrl) emit("avatar-updated", assetUrl)
    })
    .catch(() => {}) // see note above
    .finally(() => {
      uploading.value = false
    })
}
</script>

<style>
.avatar-element {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--auger-primary-light);
  background: var(--auger-bg-muted);
  color: var(--auger-primary);
  border: 1.5px solid var(--auger-border);
  padding: 2px;
  position: relative;

  /* Default (normal) */
  width: 48px;
  height: 48px;
  font-size: 0.9rem;

  &.is-large {
    width: 96px;
    height: 96px;
    font-size: 1.5rem;
    border-width: 3px;
    padding: 4px;
  }

  &.is-medium {
    width: 64px;
    height: 64px;
    font-size: 1.1rem;
    border-width: 2px;
  }

  &.is-small {
    width: 34px;
    height: 34px;
    font-size: 0.7rem;
    padding: 0;
  }

  &.is-x-small {
    width: 28px;
    height: 28px;
    font-size: 0.6rem;
    padding: 0;
    border-width: 1px;
  }

  /* A fallback icon given a color tints the whole element, not just the glyph:
     at these weights a lone colored glyph reads as an error state, while the
     ring and fill make it read as the record's own mark (same treatment as the
     type chips it stands in for). */
  &.is-tinted {
    border-color: color-mix(in srgb, var(--avatar-icon-color) 35%, transparent);
    background: color-mix(in srgb, var(--avatar-icon-color) 12%, transparent);

    .avatar-icon {
      color: var(--avatar-icon-color);
    }
  }

  &.is-drag-over {
    border-color: var(--auger-primary);
    border-style: dashed;
  }

  .avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  .avatar-initials {
    font-weight: 600;
    line-height: 0.9;
    font-size: 130%;
    user-select: none;
  }

  .avatar-content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    text-decoration: none;
    border-radius: 50%;
    overflow: hidden;
  }

  .avatar-icon {
    color: var(--auger-text-weak);
    width: 100%;
    height: 100%;

    &.is-inset {
      width: 45%;
      height: 45%;
    }
  }

  /* A glyph's detail is lost long before the circle is, so the smaller the
     avatar the more of it the inset icon fills. */
  &.is-small .avatar-icon.is-inset {
    width: 55%;
    height: 55%;
  }

  &.is-x-small .avatar-icon.is-inset {
    width: 62%;
    height: 62%;
  }

  .avatar-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.4);
    color: white;
    font-size: 1rem;
  }

  .avatar-badge {
    position: absolute;
    right: -2px;
    bottom: -2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1.5px solid var(--auger-bg);
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    overflow: hidden;
    z-index: 1;
    text-align: center;

    > * {
      width: 100%;
      height: 100%;
      border-radius: 50%;
    }
  }
}
</style>
